/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@mui/material'
import countryList from 'react-select-country-list'
import VariantsContainer from './Variant/variantsContainer'
import { CollectionApi } from '../../api/collectionApi'
import { ProductApi } from '../../api/productApi'
import { useDispatch } from 'react-redux'
import { getProduct } from '../../actions/productActions'
import { setSuccessNotification } from '../../actions/notificationAction'
import '../Collection/Collection.css'
import EditPageLowerContainer from '../Shared/EditPageLowerContainer'
import { ProductInfo, ProductVisibility, ProductStats, ProductLogs, ProductCollections, ProductMedia, SeoUrlEditor, FulfillmentMessage, OnDemandCatalogueModal } from '../../shared-components'
import useTranslation from '../../hooks/useTranslation'
import AuthStorage from '../../services/authStorage'

const EditProduct = (props) => {
  const dispatch = useDispatch()
  const Auth = new AuthStorage()

  const [term, setTerm] = useState('')
  const [collections, setCollections] = useState([])
  const [searchRequestStatus, setSearchRequestStatus] = useState('pending')
  const [selectedImages, setSelectedImages] = useState([])
  const [hasVariants, setHasVariants] = useState(props.product.has_variants)
  const [isBundle, setIsBundle] = useState(props.product.is_bundle ?? false)
  const [productUnitPrice, setProductUnitPrice] = useState(props.product.price ?? '')
  const options = useMemo(() => countryList().getData(), [])
  const [productCollections, setProductCollections] = useState([])
  const productCollectionIds = productCollections?.map((collection) => collection.id)
  const storeUrl = Auth.getStoreUrl(`/${props.product.category_slug}/${props.product.slug}`)
  const { t } = useTranslation()
  const [onDemandOpen, setOnDemandOpen] = useState(false)
  const isEdit = Number(props.product?.id) > 0
  const isNew = !isEdit

  const disabledSkus = useMemo(() => (props.product?.variants || []).map(v => v.sku).filter(Boolean), [props.product?.variants])
  const disabledSkusRef = useRef(disabledSkus)

  useEffect(() => {
    if (!onDemandOpen) disabledSkusRef.current = disabledSkus
  }, [disabledSkus, onDemandOpen])

  const handleOpenOnDemandImport = () => {
    setOnDemandOpen(true)
  }

  const handleCreateFromOnDemand = async (selectedItems = []) => {
    try {
      if (!selectedItems.length) return
      const res = await ProductApi.importOnDemandProducts({ item: selectedItems[0] })
      if (res.status === 201 && res.data?.product_id) {
        window.location.href = `/admin/products/edit/${res.data.product_id}`
        return
      }
    } finally {
      setOnDemandOpen(false)
    }
  }

  const handleSelectedImagesUpdate = (event, image) => {
    let images = [...selectedImages]
    if (event.target.checked) images.push(image.id)
    else images = images.filter((img) => img !== image.id)
    setSelectedImages([...images])
  }

  useEffect(() => {
    setHasVariants(props.product.has_variants)
  }, [props.product.has_variants])

  useEffect(() => {
    setIsBundle(props.product.is_bundle)
  }, [props.product.is_bundle])

  useEffect(() => {
    setProductUnitPrice(props.product.price)
  }, [props.product.price])

  useEffect(() => {
    if (selectedImages) setSelectedImages([])
  }, [props.setSelectedImages])

  useEffect(() => {
    setProductCollections(props.product.collections)
  }, [props.product.collections])

  const fetchCollections = () => {
    CollectionApi.searchCollections(term).then(({ status, data }) => {
      if (status === 200) {
        if (data?.data?.length === 0) setSearchRequestStatus('No Collections Found')
        const selectedProducts = data?.data.filter(
          (collection) => !productCollectionIds?.includes(collection.id)
        )
        setCollections(selectedProducts)
      }
    })
  }

  const addProductToCollection = async (collectionId, actionType) => {
    let updatedProductCollectionsIds = [...productCollectionIds]
    if (actionType === 'add') updatedProductCollectionsIds.push(collectionId)
    else updatedProductCollectionsIds = updatedProductCollectionsIds.filter((id) => id !== collectionId)

    const response = await ProductApi.addToCollection({
      collectionIds: updatedProductCollectionsIds,
      productId: props.product.id
    })

    if (response?.status === 200) {
      if (actionType === 'add') {
        const filteredCollections = collections.filter((c) => c.id === collectionId)
        setProductCollections([...response.data, ...filteredCollections])
        setCollections(collections.filter((c) => c.id !== collectionId))
        dispatch(setSuccessNotification({ message: 'Product is added to the Collection' }))
      } else {
        setProductCollections(response.data.filter((c) => c.id !== collectionId))
        dispatch(setSuccessNotification({ message: 'Product is removed from the Collection' }))
      }
      dispatch(getProduct(props.product.id))
    }
  }

  const handleClearProductError = async (productId) => {
    try {
      await ProductApi.clearFulfillmentMessage(productId)
    } catch (error) {
      console.error(error)
    }
  }

  const translateText = (text) => t(text)

  const handleDelete = () => {
    props.handleBulkImageDeletion(selectedImages)
  }

  const leftFirstContainer = () => (
    <ProductInfo
      {...props}
      storeUrl={Auth.getStoreUrl()}
      hasVariants={hasVariants}
      options={options}
      translateText={translateText}
    />
  )

  const rightFirstContainer = () => (
    <>
      <ProductVisibility {...props} storeUrl={storeUrl} translateText={translateText} />
      {isEdit && (
        <SeoUrlEditor
          baseUrl={Auth.getStoreUrl(`/${props.product.category_slug}`)}
          slug={props.product.slug}
          onChange={props.onChange}
          validationErrors={props.validationErrors}
          onSave={props.onSave}
        />
      )}
    </>
  )

  const rightStatsContainer = () => (
    <ProductStats
      productId={props.product.id}
      fetchProductStats={ProductApi.getProductStats}
      translateText={translateText}
    />
  )

  const rightSecondContainer = () => (
    <ProductCollections
      productCollections={productCollections}
      productCollectionsIds={productCollectionIds}
      addProductToCollection={addProductToCollection}
      fetchCollections={fetchCollections}
      collections={collections}
      term={term}
      setTerm={setTerm}
      translateText={translateText}
      searchRequestStatus={searchRequestStatus}
    />
  )

  const leftSecondContainer = () => (
    <ProductMedia
      product={props.product}
      updateImage={props.updateImage}
      sortImages={props.sortImages}
      newImage={props.newImage}
      removeImage={props.removeImage}
      handleSelectedImagesUpdate={handleSelectedImagesUpdate}
      selectedImages={selectedImages}
      searchRequestStatus={searchRequestStatus}
      handleBulkImageDeletion={handleDelete}
    />
  )

  const leftThirdContainer = () => (
    <Grid>
      <VariantsContainer
        productImages={props.product.images}
        onProductChange={props.onChange}
        hasVariants={hasVariants}
        setHasVariants={setHasVariants}
        isBundle={isBundle}
        setIsBundle={setIsBundle}
        unitPrice={productUnitPrice}
        variants={props.product.variants}
        onUpdate={props.updateVariant}
        sortArray={props.sortVariants}
        newVariant={props.newVariant}
        type="variants"
        pageType={isEdit ? 'Edit' : 'New'}
        fulfillmentCenter={props.product.fulfillmentCenter}
        product={props.product}
      />
    </Grid>
  )

  const rightThirdContainer = () => <ProductLogs {...props} translateText={translateText} />

  const rightFourthContainer = () => (
    <>
      {props.product.fulfillment_center_id && (
        <>
          <h4>{t('labels.fulfillment_center')}</h4>
          <div style={{ marginTop: '20px' }}>{props.product.fulfillmentCenter?.name}</div>
        </>
      )}

      {isNew && (
        <div style={{ marginTop: '12px' }}>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleOpenOnDemandImport}
          >
            Import Product from On-Demand
          </button>
        </div>
      )}

      <FulfillmentMessage
        message={props.product.fulfillment_message}
        id={props.product.id}
        onClear={handleClearProductError}
        label="Product Fulfillment Message"
      />
    </>
  )
  return (
    <form onSubmit={props.onSave}>
      <EditPageLowerContainer
        leftContent={[leftFirstContainer, leftSecondContainer, leftThirdContainer]}
        rightContent={
          isEdit
            ? [rightFirstContainer, rightStatsContainer, rightSecondContainer, rightThirdContainer, rightFourthContainer]
            : [rightFirstContainer, rightThirdContainer, rightFourthContainer]
        }
        actionType={isEdit ? 'Edit' : 'New'}
      />
    <OnDemandCatalogueModal
      isOpen={onDemandOpen}
      onClose={() => setOnDemandOpen(false)}
      mode="product"
      loadItems={({ page, count }) => ProductApi.getOnDemandCatalogue({ page, count })}
      onConfirm={handleCreateFromOnDemand}
    />
    </form>
  )
}

EditProduct.propTypes = {
  product: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired,
  validationErrors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  updateVariant: PropTypes.func.isRequired,
  updateImage: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  newImage: PropTypes.func,
  sortVariants: PropTypes.func,
  newVariant: PropTypes.func
}

export default EditProduct